// eslint-disable-next-line max-classes-per-file
import { IAttribute, INumberConstraint, IStringConstraint, ITimespanConstraint, IValidationContext, IViolation } from './annotation';
import { Named, Owned } from './named';
import { sanitizeAndFormatString } from '../../shared/utils/string.util';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { IAttributeValidator } from './IAttributeValidator';
import { Metamodel } from './model';
import { isMap, isSet } from 'lodash';

function initCaps(value: string): string {
    if (value) {
        return value.charAt(0).toLocaleUpperCase() + value.slice(1);
    }
    return '';
}

export class UniqueValidator implements IAttributeValidator {
    public static readonly INSTANCE = new UniqueValidator();

    public validate(_object: {}, attribute: IAttribute, value: Set<Named> | Named[], _owner: Owned, context: IValidationContext): IViolation[] | undefined {
        return this.validateInternal(_object, attribute, value, _owner, context);
    }

    protected validateInternal(_object: {}, attribute: IAttribute, value: Set<Named> | Named[], _owner: Owned, context: IValidationContext, caseSensitive?: boolean): IViolation[] | undefined {
        if (value == null) {
            return [];
        }
        // Reverse map the names to objects. We need to collect and report them as one violation
        // per incident
        const namedMap = new Map<string, Named[]>();
        let duplicates = false;
        for (const named of value) {
            if (!named.name) {
                continue; // that will be tracked by the required attribute
            }
            const nameToCheck = caseSensitive ? named.name : named.name.toUpperCase();
            const tuples = namedMap.get(nameToCheck);
            if (!tuples) {
                namedMap.set(nameToCheck, [named]);
            } else {
                tuples.push(named);
                duplicates = true;
            }
        }

        if (!duplicates) {
            return undefined;
        }

        const display = (attribute.primitive) ? (attribute.modelType()).display[1] : attribute.display;
        const violations: IViolation[] = [];
        for (const [name, nameds] of namedMap) {
            if (nameds.length === 1) {
                continue;
            }
            violations.push({
                context: nameds,
                message: () => sanitizeAndFormatString(shellStringResources.UniqueValidatorMessage, name, nameds.length.toString(), display),
                attributes: [attribute],
                logDetails: {
                    functionName: 'UniqueValidator.validateInternal',
                    errorType: 'DuplicatedName'
                }
            });
        }
        return violations;
    }
}

export class UniqueCaseSensitiveValidator extends UniqueValidator implements IAttributeValidator {
    public static readonly INSTANCE = new UniqueCaseSensitiveValidator();

    public validate(_object: {}, attribute: IAttribute, value: Set<Named> | Named[], _owner: Owned, context: IValidationContext): IViolation[] | undefined {
        return this.validateInternal(_object, attribute, value, _owner, context, true);
    }
}

export class RequiredValidator implements IAttributeValidator {
    public static readonly INSTANCE = new RequiredValidator();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static isEmpty(value: any): boolean {
        if (value === undefined || value === null) {
            return true;
        } else if (typeof value === 'string') {
            return value.length === 0 || value.trim().length === 0;
        } else if (Array.isArray(value)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (value as any[]).length === 0;
        } else if (isSet(value)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (value as Set<any>).size === 0;
        } else if (isMap(value)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (value as Map<any, any>).size === 0;
        }
        return false;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public validate(object: {}, attribute: IAttribute, value: Set<Named> | Named[], owner: Owned, context: IValidationContext): IViolation[] | undefined {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const requiredAttributes = attribute.constraint!.required;
        const model = Metamodel.modelOf(object);
        if (Array.isArray(requiredAttributes)) {
            let required = false;
            // Check if any of the required attributes are needed or not
            for (const requiredAttribute of requiredAttributes) {
                const reqAttribute = requiredAttribute.startsWith('!') ?
                    requiredAttribute.substring(1) : requiredAttribute;
                if (!model.hasAttribute(reqAttribute)) {
                    continue;
                }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const requiredValue = (object as any)[reqAttribute] ||
                    model.attribute(reqAttribute).templateValue(object);
                // Don't recursively call it since it might be mutually dependent
                if (requiredAttribute.startsWith('!')) {
                    if (RequiredValidator.isEmpty(requiredValue) || requiredValue === false) {
                        required = true;
                        break;
                    }
                } else {
                    if (!RequiredValidator.isEmpty(requiredValue) && requiredValue !== false) {
                        required = true;
                        break;
                    }
                }
            }

            if (!required) {
                return undefined;
            }
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const named = owner.named!;
        // display may be []
        const modelType = Metamodel.modelOf(named).display[0] || '';
        const modelName = named && named.name ? `'${named.name}'` : '';
        const messageText = sanitizeAndFormatString(shellStringResources.RequiredValidatorMessageNoVowel, modelType, modelName, attribute.display);
        // required property could be fixed by expressions
        if (RequiredValidator.isEmpty(value) && attribute.templateValue(object) == null) {
            return [{
                context: owner,
                message: () => messageText,
                attributes: [attribute],
                messageType: 'missing',
                logDetails: {
                    functionName: 'RequiredValidator.validate',
                    errorType: 'Required'
                }
            }];
        }
        return undefined;
    }
}

export class PatternValidator implements IAttributeValidator {

    public static readonly INSTANCE = new PatternValidator();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public validate(_object: {}, attribute: IAttribute, value: string, owner: Owned, context: IValidationContext, constraint?: IStringConstraint): IViolation[] | undefined {
        const sConstraint = (constraint || attribute.constraint) as IStringConstraint;

        if (value == null || value === '') { // empty value should be checked by required.
            return undefined;
        }

        if (owner.named == null) {
            return undefined;
        }

        if (sConstraint.patterns && sConstraint.patterns.length > 0) {
            return this._validatePatternWithMessage(_object, attribute, value, owner, context, constraint);
        }

        if (!sConstraint.pattern) {
            return undefined;
        }

        const named = owner.named;

        const namedDisplay = initCaps(Metamodel.modelOf(named).display[0]);

        if (!sConstraint.pattern.test(value)) {
            return [{
                context: owner,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                message: () => sanitizeAndFormatString(shellStringResources.PatternValidatorMessage, namedDisplay, attribute.display, value, sConstraint.pattern!.toString()),
                attributes: [attribute],
                logDetails: {
                    functionName: 'PatternValidator.validate',
                    errorType: 'Pattern'
                }
            }];
        }
        return undefined;
    }

    public _validatePatternWithMessage(_object: {}, attribute: IAttribute, value: string, owner: Owned, context: IValidationContext, constraint?: IStringConstraint): IViolation[] | undefined {
        const sConstraint = (constraint || attribute.constraint) as IStringConstraint;

        const named = owner.named;
        const namedDisplay = initCaps(Metamodel.modelOf(named).display[0]);

        let isInvalid = false;
        let msg = '';
        for (const pattern of sConstraint.patterns) {
            if (!pattern.regex.test(value)) {
                isInvalid = true;
                msg += pattern.message + '\n';
            }
        }

        if (isInvalid) {
            return [{
                context: owner,
                message: () => sanitizeAndFormatString(shellStringResources.PatternValidatorListMessage, namedDisplay, attribute.display, value) + msg,
                attributes: [attribute],
                logDetails: {
                    functionName: 'PatternValidator._validatePatternWithMessage',
                    errorType: 'Pattern'
                }
            }];
        }
        return undefined;
    }
}

export class AllowedValidator implements IAttributeValidator {

    public static readonly INSTANCE = new AllowedValidator();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public validate(_object: {}, attribute: IAttribute, value: string, owner: Owned, context: IValidationContext): IViolation[] | undefined {
        const sConstraint = attribute.constraint as IStringConstraint;
        if (!sConstraint.allowed) {
            return undefined;
        }

        if (value == null) { // empty value should be checked by required.
            return undefined;
        }

        if (owner.named == null) {
            return undefined;
        }
        const named = owner.named;
        const namedDisplay = initCaps(Metamodel.modelOf(named).display[0]);
        if (!sConstraint.allowed.find((allowed) => allowed === value)) {
            return [{
                context: owner,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                message: () => sanitizeAndFormatString(shellStringResources.AllowedValidatorMessage, namedDisplay, attribute.display, value, sConstraint.allowed!.toString()),
                attributes: [attribute],
                logDetails: {
                    functionName: 'AllowedValidator.validate',
                    errorType: 'Range'
                }
            }];
        }
        return undefined;
    }
}

export class WholeNumberValidator implements IAttributeValidator {

    public static readonly INSTANCE = new WholeNumberValidator();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public validate(_object: {}, attribute: IAttribute, value: number, owner: Owned, context: IValidationContext): IViolation[] | undefined {
        const sConstraint = attribute.constraint as INumberConstraint;
        if (sConstraint.whole === undefined) {
            return undefined;
        }
        if (value == null) { // check undefined by required
            return undefined;
        }

        if (owner.named == null) {
            return undefined;
        }
        const named = owner.named;
        const namedDisplay = initCaps(Metamodel.modelOf(named).display[0]);
        if (!Number.isInteger(value)) {
            return [{
                context: owner,
                message: () => sanitizeAndFormatString(shellStringResources.WholeNumberValidatorMessage, namedDisplay, attribute.display, value ? value.toString() : ''),
                attributes: [attribute],
                logDetails: {
                    functionName: 'WholeNumberValidator.validate',
                    errorType: 'InvalidType'
                }
            }];
        }
        return undefined;
    }
}

export class MinMaxValidator implements IAttributeValidator {
    public static readonly INSTANCE = new MinMaxValidator();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public validate(_object: {}, attribute: IAttribute, value: number, owner: Owned, context: IValidationContext): IViolation[] | undefined {
        const sConstraint = attribute.constraint as INumberConstraint;
        if (sConstraint.minimum === undefined && sConstraint.maximum === undefined) {
            return undefined;
        }

        if (owner.named == null) {
            return undefined;
        }
        const named = owner.named;
        const namedDisplay = initCaps(Metamodel.modelOf(named).display[0]);
        let messageText: string;
        if (sConstraint.minimum !== undefined && sConstraint.maximum !== undefined &&
            (value < sConstraint.minimum || value > sConstraint.maximum)) {
            messageText = sanitizeAndFormatString(shellStringResources.MinMaxValidatorMessageBetween,
                namedDisplay, attribute.display, value.toString(), sConstraint.minimum.toString(), sConstraint.maximum.toString());
        } else if (sConstraint.minimum !== undefined && value < sConstraint.minimum) {
            messageText = sanitizeAndFormatString(shellStringResources.MinMaxValidatorMessageMinimum, namedDisplay, attribute.display, value.toString(), sConstraint.minimum.toString());
        } else if (sConstraint.maximum !== undefined && value > sConstraint.maximum) {
            messageText = sanitizeAndFormatString(shellStringResources.MinMaxValidatorMessageMaximum, namedDisplay, attribute.display, value.toString(), sConstraint.maximum.toString());
        }
        if (messageText) {
            return [{
                context: owner,
                message: () => messageText,
                attributes: [attribute],
                logDetails: {
                    functionName: 'MinMaxValidator.validate',
                    errorType: 'Range'
                }
            }];
        }
        return undefined;
    }
}

export class OccursValidator implements IAttributeValidator {

    public static readonly INSTANCE = new OccursValidator();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public validate(_object: {}, attribute: IAttribute, value: Set<any> | any[], owner: Owned, context: IValidationContext): IViolation[] | undefined {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const minOccurs = attribute.constraint!.minOccurs || 0;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const maxOccurs = attribute.constraint!.maxOccurs || Infinity;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const size = isSet(value) ? (value as Set<any>).size : (value as any[]).length;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const named = owner.named!;
        const namedDisplay = initCaps(Metamodel.modelOf(named).display[0]);

        const typeDisplay = (attribute.primitive || attribute.collection) ? attribute.modelType().display[0] : attribute.display;
        const typeDisplays = (attribute.primitive || attribute.collection) ? attribute.modelType().display[1] : attribute.display;

        let messageText: string;

        if (size < minOccurs) {
            messageText = (minOccurs === 1)
                ? sanitizeAndFormatString(shellStringResources.OccursValidatorMessageMinOne, namedDisplay, named.name, typeDisplay)
                : sanitizeAndFormatString(shellStringResources.OccursValidatorMessageMin, namedDisplay, named.name, minOccurs.toString(), typeDisplays);
        } else if (size > maxOccurs) {
            messageText = (maxOccurs === 1)
                ? sanitizeAndFormatString(shellStringResources.OccursValidatorMessageMaxOne, namedDisplay, named.name, typeDisplay)
                : sanitizeAndFormatString(shellStringResources.OccursValidatorMessageMax, namedDisplay, named.name, maxOccurs.toString(), typeDisplays);
        }
        if (messageText) {
            return [{
                context: owner,
                message: () => messageText,
                attributes: [attribute],
                logDetails: {
                    functionName: 'OccursValidator.validate',
                    errorType: 'Range'
                }
            }];
        }
        return undefined;
    }
}

export class TimespanValidator implements IAttributeValidator {
    public static readonly INSTANCE = new TimespanValidator();
    private readonly timeoutValidation: RegExp = /^(\d+\.)?((0?\d)|(1\d)|(2[0-3]))(:[0-5]?\d){2}$/;

    public static toSeconds(timespan: string): number {
        const timelist = timespan.split(/[:.]+/).reverse();
        const timescale = [86400, 3600, 60, 1];
        let seconds = 0;
        timelist.slice().forEach((value: string, index: number, array: string[]) => {
            seconds += parseInt(value, 10) * timescale.pop();
        }
        );
        return seconds;
    }

    public validate(_object: {}, attribute: IAttribute, value: string, owner: Owned, context: IValidationContext): IViolation[] | undefined {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tConstraint = attribute.constraint as ITimespanConstraint;

        if (owner.named == null || value == null) {
            return undefined;
        }

        const named = owner.named;
        const namedDisplay = initCaps(Metamodel.modelOf(named).display[0]);

        if (!this.timeoutValidation.test(value) || value.length < 1) {
            return [{
                context: owner,
                message: () => sanitizeAndFormatString(shellStringResources.TimespanValidatorInvalidFormat, attribute.display),
                attributes: [attribute],
                logDetails: {
                    functionName: 'TimespanValidator.validate',
                    errorType: 'InvalidType'
                }
            }];
        }

        const tSeconds = TimespanValidator.toSeconds(value);
        const tMin = tConstraint.minimum_timespan != null ? TimespanValidator.toSeconds(tConstraint.minimum_timespan) : undefined;
        const tMax = tConstraint.maximum_timespan != null ? TimespanValidator.toSeconds(tConstraint.maximum_timespan) : undefined;

        if (tMin === undefined && tMax === undefined) {
            return undefined;
        }

        let messageText: string;

        if (tMin !== undefined && tMax !== undefined &&
            (tSeconds < tMin || tSeconds > tMax)) {
            messageText = sanitizeAndFormatString(shellStringResources.MinMaxValidatorMessageBetween,
                namedDisplay, value, tConstraint.minimum_timespan, tConstraint.maximum_timespan);
        } else if (tMin !== undefined && tSeconds < tMin) {
            messageText = sanitizeAndFormatString(shellStringResources.TimespanValidatorMinimum, attribute.display, value, tConstraint.minimum_timespan);
        } else if (tMax !== undefined && tSeconds > tMax) {
            messageText = sanitizeAndFormatString(shellStringResources.TimespanValidatorMaximum, attribute.display, value, tConstraint.maximum_timespan);
        }
        if (messageText) {
            return [{
                context: owner,
                message: () => messageText,
                attributes: [attribute],
                logDetails: {
                    functionName: 'TimespanValidator.validate',
                    errorType: 'Range'
                }
            }];
        }
        return undefined;
    }
}

export class HmsTimespanValidator implements IAttributeValidator {
    public static readonly INSTANCE = new HmsTimespanValidator();
    private readonly hmsValidation: RegExp = /^((0?\d)|(1\d)|(2[0-3])|(24)):([0-5]\d):([0-5]\d)$/;

    public static toSeconds(timespan: string): number {
        return TimespanValidator.toSeconds(timespan);
    }

    public validate(
        _object: {},
        attribute: IAttribute,
        value: string,
        owner: Owned,
        context: IValidationContext
    ): IViolation[] | undefined {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tConstraint = attribute.constraint as ITimespanConstraint;

        if (owner.named == null || value == null) {
            return undefined;
        }

        const named = owner.named;
        const namedDisplay = initCaps(Metamodel.modelOf(named).display[0]);

        if (!this.hmsValidation.test(value) || value.length < 1) {
            return [{
                context: owner,
                message: () => sanitizeAndFormatString(
                    shellStringResources.TimespanValidatorInvalidFormat,
                    attribute.display
                ),
                attributes: [attribute],
                logDetails: {
                    functionName: 'HmsTimespanValidator.validate',
                    errorType: 'InvalidType'
                }
            }];
        }

        const tSeconds = HmsTimespanValidator.toSeconds(value);
        const tMin = tConstraint.minimum_timespan != null
            ? HmsTimespanValidator.toSeconds(tConstraint.minimum_timespan)
            : undefined;
        const tMax = tConstraint.maximum_timespan != null
            ? HmsTimespanValidator.toSeconds(tConstraint.maximum_timespan)
            : undefined;

        if (tMin === undefined && tMax === undefined) {
            return undefined;
        }

        let messageText: string | undefined;

        if (tMin !== undefined && tMax !== undefined &&
            (tSeconds < tMin || tSeconds > tMax)) {
            messageText = sanitizeAndFormatString(
                shellStringResources.MinMaxValidatorMessageBetween,
                namedDisplay, value, tConstraint.minimum_timespan, tConstraint.maximum_timespan
            );
        } else if (tMin !== undefined && tSeconds < tMin) {
            messageText = sanitizeAndFormatString(
                shellStringResources.TimespanValidatorMinimum,
                attribute.display, value, tConstraint.minimum_timespan
            );
        } else if (tMax !== undefined && tSeconds > tMax) {
            messageText = sanitizeAndFormatString(
                shellStringResources.TimespanValidatorMaximum,
                attribute.display, value, tConstraint.maximum_timespan
            );
        }

        if (messageText) {
            return [{
                context: owner,
                message: () => messageText!,
                attributes: [attribute],
                logDetails: {
                    functionName: 'HmsTimespanValidator.validate',
                    errorType: 'Range'
                }
            }];
        }

        return undefined;
    }
}

